/* istanbul ignore file */
import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { CitywireInternalAccountsService } from '@tpapa/citywire-internal-accounts-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppConfig } from 'src/app/app-config';
import { UserEvent } from '../enums/user-event';
import { UserState } from 'src/app/core/models/i-user-state';
import { UserAlert } from 'src/app/core/models/i-user-alert';

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  hubUrl: string;
  connection: HubConnection;
  private usersSub: BehaviorSubject<UserState[]> = new BehaviorSubject([]);
  private userAlertSub: BehaviorSubject<UserAlert> = new BehaviorSubject(
    new UserAlert()
  );
  private userStateSub: BehaviorSubject<UserState> = new BehaviorSubject(
    new UserState()
  );
  private areListenersReadySub$ = new BehaviorSubject(false);
  authToken: string;

  authService: CitywireInternalAccountsService;

  areListenersReady$ = this.areListenersReadySub$.asObservable();
  users$: Observable<UserState[]> = this.usersSub.asObservable();
  userAlert$ = this.userAlertSub.asObservable();

  constructor(
    private appConfig: AppConfig,
    authService: CitywireInternalAccountsService
  ) {
    this.hubUrl = `${this.appConfig.cmsApiBaseUrl}/api/userhub`;
    this.authService = authService;
  }

  initiateSignalrConnection() {
    // The different configurations are needed for it to work on both local and remote envs
    if (this.appConfig.environment === 'dev') {
      // Log is added for easy detection if deployed incorrectly
      console.log('Warn: Websocket running in local config');
      this.connection = new HubConnectionBuilder()
        .withUrl(this.hubUrl, {
          accessTokenFactory: () => this.authService.getUserJwt(),
        })
        .withAutomaticReconnect()
        .build();
    } else {
      this.connection = new HubConnectionBuilder()
        .withUrl(this.hubUrl, {
          accessTokenFactory: () => this.authService.getUserJwt(),
          skipNegotiation: true,
          transport: 1,
        })
        .withAutomaticReconnect()
        .build();
    }

    this.connection.start().then(() => {
      this.setSignalrClientMethods();
    });
  }

  userJoinedPage(username: string, email: string, contentId: string) {
    this.connection.send('join', username, email, contentId);
  }

  userLeftPage(username: string, contentId: string) {
    this.connection.send('leave', username, contentId);
  }

  userEditedPage(username: string, contentId: string) {
    this.connection.send('editing', username, contentId);
  }

  userSavedPage(username: string, contentId: string) {
    this.connection.send('saved', username, contentId);
  }

  private setSignalrClientMethods(): void {
    this.connection.on('userList', (result: UserState[]) => {
      this.usersSub.next(result);
    });

    this.connection.on('userSaved', (result) => {
      const alert = new UserAlert(
        result.contentId,
        result.username,
        UserEvent.saved
      );
      this.userAlertSub.next(alert);
    });

    this.areListenersReadySub$.next(true);
  }
}
